import { useMemo } from 'react';

import { Box } from '@aicon-dev/caas-core';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

export interface RfiListItemThumbnailProps {
  thumbnailUrls: string[];
}

export const RfiListItemThumbnail = (props: RfiListItemThumbnailProps) => {
  const { thumbnailUrls } = props;
  const { t } = useTranslation('report');

  const className = useMemo(() => {
    if (thumbnailUrls?.length > 4) return 'type-A';
    switch (thumbnailUrls?.length) {
      case 4:
        return 'type-A';
      case 3:
        return 'type-B';
      case 2:
        return 'type-C';
      case 1:
      default:
        return 'type-D';
    }
  }, [thumbnailUrls.length]);

  if (!thumbnailUrls.length) return null;

  return (
    <ThumbnailWrap className={className}>
      <Box className="grid" position="absolute" top={0} left={0} width="100%" height="100%">
        {thumbnailUrls.slice(0, 4).map((url, index) => {
          const isLastForTypeA = className === 'type-A' && index === 3 && thumbnailUrls.length > 4;

          return (
            <ThumbnailCoverStyled key={url} className="grid-item">
              <img src={url} width="100%" height="100%" />
              {isLastForTypeA && (
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  width="100%"
                  height="100%"
                  color="white"
                  fontSize="18px"
                  fontWeight={500}
                  opacity={0.7}
                  background="#0000004D"
                >
                  +
                  <Trans
                    t={t}
                    i18nKey="report:m_report_thumbnail_length_text"
                    values={{
                      length: thumbnailUrls.length - 4,
                    }}
                  />
                </Box>
              )}
            </ThumbnailCoverStyled>
          );
        })}
      </Box>
    </ThumbnailWrap>
  );
};

const ThumbnailWrap = styled.div`
  position: relative;
  display: block;
  padding-bottom: 49%;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 8px;

  & > .grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    grid-template-rows: repeat(2, 1fr);
    gap: 8px;
  }

  &.type-A > .grid {
    grid-template-columns: repeat(3, 2fr, 1fr, 1fr);
    grid-template-rows: repeat(2, 1fr);
    .grid-item:nth-child(1) {
      grid-column: 1 / 3;
      grid-row: 1 / 3;
      &::after,
      img {
        border-radius: 4px 0 0 4px;
      }
    }
    .grid-item:nth-child(2) {
      grid-column: 3 / 5;
      &::after,
      img {
        border-radius: 0 4px 0 0;
      }
    }
  }
  &.type-B > .grid {
    grid-template-columns: repeat(2, 1fr);
    .grid-item:nth-child(1) {
      grid-column: 1 / 2;
      grid-row: 1 / 3;
      &::after,
      img {
        border-radius: 4px 0 0 4px;
      }
    }
    .grid-item:nth-child(2) {
      &::after,
      img {
        border-radius: 0 4px 0 0;
      }
    }
    .grid-item:nth-child(3) {
      &::after,
      img {
        border-radius: 0 0 4px 0;
      }
    }
  }
  &.type-C > .grid {
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: 1fr;
    .grid-item:nth-child(1) {
      &::after,
      img {
        border-radius: 4px 0 0 4px;
      }
    }
    .grid-item:nth-child(2) {
      &::after,
      img {
        border-radius: 0 4px 4px 0;
      }
    }
  }
  &.type-D > .grid {
    /* display: block; */
    grid-template-columns: 1fr;
    grid-template-rows: 1fr;
    .grid-item {
      &::after,
      img {
        border-radius: 4px;
      }
    }
  }
`;

const ThumbnailCoverStyled = styled.div`
  position: relative;
  background-color: #d9d9d9;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.05) inset;
  }

  .lazy-load-image-background.opacity {
    opacity: 0;
    transition: opacity 0.3s;
    &.lazy-load-image-loaded {
      opacity: 1;
    }
  }

  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
  }
`;
